/**
 * The set row's stepper maths (`D110`, `T48`) — what a tap on `−`/`+` does to
 * the number, and what a keypad entry is allowed to become.
 *
 * ── PURE, LIKE EVERYTHING IN `src/lib/session/` ─────────────────────────────
 * The mobile vitest project runs `src/**\/__tests__/*.test.ts` in plain Node;
 * a React import here would move these rules onto a phone. The components in
 * `components/session/` render what these functions decide.
 *
 * ── `load: null` IS BODYWEIGHT AND NEVER BECOMES `0` ────────────────────────
 * Nothing in this file manufactures a load for a bodyweight item. The set row
 * simply renders no load stepper when the suggestion's load is `null`, and
 * `null` goes on the wire as `null` (`record-workout-set.ts`).
 *
 * ── A SET IS SECONDS OR METRES AS OFTEN AS IT IS REPS (`D151`) ──────────────
 * 743 catalogue exercises track duration or distance rather than reps, and the
 * row chooses its controls from the exercise's `tracks` ternary
 * (`exerciseTracking`: 0 not tracked, 1 optional, 2 required). `setControls`
 * is that choice, written once so the row and its tests agree; the two parsers
 * beside it read what the keypad hands back for a hold and a distance.
 */
/** The plates most gyms actually have on both ends: 1.25 kg each side. A
 *  plan item whose equipment implies a different step passes its own. */
export const DEFAULT_LOAD_STEP_KG = 2.5;
/** One `−`/`+` tap on the load. Clamped at zero — an empty bar is the floor,
 *  and a negative load is a claim nothing downstream can read. */
export function stepLoad(current, direction, stepKg = DEFAULT_LOAD_STEP_KG) {
    const next = current + direction * stepKg;
    // Steps are quarter-kilo aligned in practice; rounding kills the float
    // drift of repeated 2.5 additions (`0.30000000000000004`-style noise).
    return Math.max(0, Math.round(next * 100) / 100);
}
/** One `−`/`+` tap on the reps. Always ±1, clamped at zero. */
export function stepReps(current, direction) {
    return Math.max(0, current + direction);
}
/**
 * A keypad load — the long-press escape hatch (`D110`).
 *
 * `"82,5"` is accepted because the first locale is pt-BR (`D101`) and the
 * numeric keyboard there offers a comma. Anything that is not a finite,
 * non-negative number is `null`, and the caller keeps the previous value —
 * a garbled entry must not zero a loaded bar.
 */
export function parseLoadInput(text) {
    const trimmed = text.trim().replace(",", ".");
    if (trimmed === "") {
        return null;
    }
    const value = Number(trimmed);
    if (!Number.isFinite(value) || value < 0) {
        return null;
    }
    return Math.round(value * 100) / 100;
}
const WHOLE_NUMBER = /^\d+$/;
/** A keypad rep count: a whole, non-negative number or nothing. */
export function parseRepsInput(text) {
    const trimmed = text.trim();
    if (!WHOLE_NUMBER.test(trimmed)) {
        return null;
    }
    return Number(trimmed);
}
/** `D151`: the engine progresses a hold by five seconds, so a tap does too. */
export const DURATION_STEP_SECONDS = 5;
/** `D151`: a duration-tracked pick starts at thirty seconds. The row falls
 *  back to it when an item carries no `targetDurationSeconds` of its own. */
export const DEFAULT_HOLD_SECONDS = 30;
/** One `−`/`+` tap on a hold, clamped at zero. */
export function stepDuration(current, direction, stepSeconds = DURATION_STEP_SECONDS) {
    return Math.max(0, current + direction * stepSeconds);
}
const MINUTES_SECONDS = /^(\d+):(\d{1,2})$/;
/**
 * A keypad hold: `"45"` is seconds, `"1:30"` is minutes and seconds. Anything
 * else — a decimal, a negative, `"1:75"` — is `null` and the caller keeps the
 * previous value, as with the other two parsers.
 */
export function parseDurationInput(text) {
    const trimmed = text.trim();
    if (WHOLE_NUMBER.test(trimmed)) {
        return Number(trimmed);
    }
    const clock = MINUTES_SECONDS.exec(trimmed);
    if (clock === null) {
        return null;
    }
    const seconds = Number(clock[2]);
    if (seconds >= 60) {
        return null;
    }
    return Number(clock[1]) * 60 + seconds;
}
/**
 * A keypad distance in metres, with the pt-BR comma accepted for the same
 * reason `parseLoadInput` accepts it. Rounded to the centimetre.
 */
export function parseDistanceInput(text) {
    return parseLoadInput(text);
}
/**
 * What the next hold is pre-filled with: the last one logged, else the item's
 * target, else the engine's own opening number. No RPE rule in here — a hold
 * progresses between sessions on the server (`D151`), and within a bout the
 * honest default is *"the same again"*.
 */
export function nextHoldSeconds(last, target) {
    return last ?? target ?? DEFAULT_HOLD_SECONDS;
}
/**
 * A row without `tracks` predates `D151` on the wire, and every row until
 * then was reps and weight — so that is what its absence means here.
 */
export const DEFAULT_TRACKS = {
    distance: 0,
    duration: 0,
    reps: 2,
    weight: 1,
};
export function setControls(tracks = DEFAULT_TRACKS) {
    return {
        distance: tracks.distance > 0,
        hold: tracks.duration > 0 && tracks.reps === 0,
        load: tracks.weight > 0,
        reps: tracks.reps > 0,
    };
}
/**
 * The second number of a set row — the one beside the load. A hold where
 * {@link SetControls.hold} says so, metres where distance is tracked and reps
 * are not, reps otherwise. The live log view and the finished-workout log
 * both read it, so one exercise is never logged as reps on one screen and as
 * a hold on the other.
 */
export function secondMeasureOf(controls) {
    if (controls.hold) {
        return "hold";
    }
    if (controls.distance && !controls.reps) {
        return "distance";
    }
    return "reps";
}
/**
 * A measure the row did not take goes on the wire as `null`, never `0`: the
 * contract wants a hold and a distance positive or absent, and a `0` here is
 * exactly the `× 0` the logged row must never show (`D151`).
 */
export function measureOrNull(value) {
    return value === null || value <= 0 ? null : value;
}
//# sourceMappingURL=set-entry.js.map